import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Search, Filter, Eye, Edit3, X } from "lucide-react";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import api from '../services/api';
import { ordersService } from '../services/orders';
import OrderInfoEditModal from '../components/OrderInfoEditModal';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/Dialog";
import { Label } from "../components/ui/Label";
import { AsyncSelect } from "../components/AsyncSelect";
import { userService } from "../services/users";

export function VendasPage() {
  const [sales, setSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  if (totalItems < 0) console.log(totalItems);

  const [admins, setAdmins] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    api.get('/api/admin?per_page=-1').then(res => {
      const list = res.data?.data?.data || res.data?.data || res.data || [];
      setAdmins(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [todaySalesCount, setTodaySalesCount] = useState(0);
  const [avgTicket, setAvgTicket] = useState(0);
  const [mode, setMode] = useState<'day' | 'search'>('day');
  const [targetDate, setTargetDate] = useState<string | null>(null);

  // Estados do Rastreio
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [shippingOrderId, setShippingOrderId] = useState<number | null>(null);
  const [shippingCode, setShippingCode] = useState("");
  const [isSubmittingShipping, setIsSubmittingShipping] = useState(false);

  // Estados dos Filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    number: '',
    sku: '',
    customer: '',
    salesman: '',
    status: '',
    gtway: '',
    dateFrom: '',
    dateTo: '',
  });
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [customerLabel, setCustomerLabel] = useState('');

  const fetchSales = async (page = 1, search = '', activeFilt = {}) => {
    setLoading(true);
    try {
      const response = await ordersService.getOrders(page, search, 15, activeFilt);
      if (response.success) {
        setSales(response.data || []);
        setCurrentPage(response.meta.current_page || 1);
        setTotalPages(response.meta.last_page || 1);
        setTotalItems(response.meta.total || 0);
        setTodayRevenue(response.meta.day_revenue || 0);
        setTodaySalesCount(response.meta.day_sales_count || 0);
        setAvgTicket(response.meta.avg_ticket || 0);
        setMode(response.meta.mode || 'day');
        setTargetDate(response.meta.target_date || null);
      }
    } catch (error) {
      console.error("Erro ao buscar vendas:", error);
      toast.error("Erro ao carregar a lista de vendas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales(1, searchTerm, activeFilters);
  }, [searchTerm, activeFilters]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchSales(page, searchTerm, activeFilters);
    }
  };

  const handleOpenEditModal = (order: any) => {
    setSelectedOrder(order);
    setIsEditModalOpen(true);
  };

  const handleAbrirShipping = (sale: any) => {
    setShippingOrderId(sale.id);
    setShippingCode(sale.shipping_code || "");
    setShippingDialogOpen(true);
  };

  const handleConfirmarShipping = async () => {
    if (!shippingOrderId) return;
    setIsSubmittingShipping(true);
    try {
      const response = await ordersService.updateTracking(shippingOrderId, shippingCode);
      if (response.success) {
        toast.success("Rastreio updated com sucesso!");
        setShippingDialogOpen(false);
        fetchSales(currentPage, searchTerm, activeFilters);
      }
    } catch (error) {
      console.error("Erro ao registrar rastreio:", error);
      toast.error("Erro ao registrar rastreio.");
    } finally {
      setIsSubmittingShipping(false);
    }
  };

  const handleApplyFilters = () => {
    // Filtra apenas chaves com valor preenchido
    const cleanedFilters = Object.entries(filters).reduce((acc, [key, val]) => {
      if (val) acc[key] = val;
      return acc;
    }, {} as Record<string, any>);

    setActiveFilters(cleanedFilters);
    toast.success("Filtros aplicados!");
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      number: '',
      sku: '',
      customer: '',
      salesman: '',
      status: '',
      gtway: '',
      dateFrom: '',
      dateTo: '',
    };
    setFilters(emptyFilters);
    setCustomerLabel('');
    setActiveFilters({});
    toast.success("Filtros limpos!");
  };

  // Estatísticas finais lidas diretamente da API (sem cálculos no frontend)
  const totalSalesToShow = todaySalesCount;
  const revenueToShow = todayRevenue;
  const avgTicketToShow = avgTicket;

  const dateLabel = mode === 'day' && targetDate ? ` (${targetDate})` : ' (Busca)';
  const descSuffix = mode === 'day' ? 'no dia selecionado' : 'nos resultados encontrados';

  // Helper para mostrar quais tags de filtro estão ativas
  const getActiveFilterTags = () => {
    const tags: { key: keyof typeof filters; label: string }[] = [];
    if (activeFilters.number) tags.push({ key: 'number', label: `Nº Pedido: ${activeFilters.number}` });
    if (activeFilters.sku) tags.push({ key: 'sku', label: `SKU: ${activeFilters.sku}` });
    if (activeFilters.customer) {
      const displayLabel = customerLabel || activeFilters.customer;
      tags.push({ key: 'customer', label: `Cliente: ${displayLabel}` });
    }
    if (activeFilters.salesman) {
      const selectedAdminObj = admins.find(a => String(a.id) === String(activeFilters.salesman));
      const adminName = selectedAdminObj ? selectedAdminObj.name : activeFilters.salesman;
      const isInactive = selectedAdminObj && (selectedAdminObj.is_active === false || selectedAdminObj.deleted_at);
      tags.push({ key: 'salesman', label: `Vendedora: ${adminName}${isInactive ? ' (Inativa)' : ''}` });
    }
    if (activeFilters.status) {
      const statusNames: Record<string, string> = {
        'all': 'Todos os status',
        '5,7': 'Completo & Cancelado',
        '5': 'Completo',
        '7': 'Cancelado',
        '2': 'Em Processamento / Pendente'
      };

      if (statusNames[activeFilters.status]) {
        tags.push({ key: 'status', label: `Status: ${statusNames[activeFilters.status]}` });
      } else {
        const parts = String(activeFilters.status).split(',').map((id: string) => {
          if (id === '5') return 'Completo';
          if (id === '7') return 'Cancelado';
          if (id === '2') return 'Em Processamento';
          return id;
        });
        tags.push({ key: 'status', label: `Status: ${parts.join(', ')}` });
      }
    }
    if (activeFilters.gtway) {
      const gatewaysMap: Record<string, string> = {
        '2': 'Cartão de Crédito', '3': 'Dinheiro', '4': 'Paypal (link)', '5': 'Depósito',
        '6': 'Dinheiro + Depósito', '7': 'Dinheiro + Cartão', '8': 'Dinheiro + Cheque',
        '9': 'Depósito + Cartão (Loja)', '10': 'Crédito Cliente (Loja)', '11': 'Crédito Fornecedora (Loja)',
        '12': 'Depósito + link PayPal', '13': 'Desconto / Fora do Closet'
      };
      tags.push({ key: 'gtway', label: `Pagamento: ${gatewaysMap[activeFilters.gtway] || activeFilters.gtway}` });
    }
    if (activeFilters.dateFrom) tags.push({ key: 'dateFrom', label: `De: ${activeFilters.dateFrom}` });
    if (activeFilters.dateTo) tags.push({ key: 'dateTo', label: `Até: ${activeFilters.dateTo}` });
    return tags;
  };

  const removeSingleFilter = (key: keyof typeof filters) => {
    if (key === 'customer') {
      setCustomerLabel('');
    }
    const updatedFilters = { ...filters, [key]: '' };
    setFilters(updatedFilters);

    const cleanedFilters = Object.entries(updatedFilters).reduce((acc, [k, val]) => {
      if (val) acc[k] = val;
      return acc;
    }, {} as Record<string, any>);

    setActiveFilters(cleanedFilters);
  };

  return (
    <main className="flex-1 p-6 bg-gray-50 text-[#68686A]">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Vendas</h1>
        <p>Gerencie todas as suas vendas</p>
      </div>

      {/* Busca e Barra de Filtros */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Buscar vendas por ID, cliente ou produto..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-300 bg-white focus:ring-2 focus:ring-[#D4C070]"
          />
        </div>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          onClick={() => setShowFilters(!showFilters)}
          className={`border-gray-300 ${showFilters ? 'bg-[#D4C070] text-white hover:bg-[#849D70]' : 'hover:bg-[#EEF1F2]'}`}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filtros
          {Object.keys(activeFilters).length > 0 && (
            <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-emerald-200">
              {Object.keys(activeFilters).length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Filtros Ativos (Tags) */}
      {getActiveFilterTags().length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs text-gray-500 mr-1 font-semibold">Filtros ativos:</span>
          {getActiveFilterTags().map(tag => (
            <Badge key={tag.key} variant="secondary" className="bg-[#D4C070]/20 text-gray-800 border-none flex items-center gap-1 font-normal py-1 text-xs">
              {tag.label}
              <button
                type="button"
                onClick={() => removeSingleFilter(tag.key)}
                className="ml-1 text-gray-500 hover:text-red-500 transition-colors focus:outline-none flex items-center justify-center"
              >
                <X className="h-3 w-3 cursor-pointer" />
              </button>
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-gray-500 h-6 px-2 hover:bg-transparent hover:underline"
            onClick={handleClearFilters}
          >
            Limpar todos
          </Button>
        </div>
      )}

      {/* Popup de filtros */}
      {showFilters && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative border border-gray-200 shadow-xl max-h-[95vh] overflow-y-auto animate-in fade-in zoom-in-95">
            <button
              onClick={() => setShowFilters(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">Filtrar Vendas</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-semibold">Nº do Pedido (Ref)</label>
                <Input
                  placeholder="Ex: 145"
                  value={filters.number}
                  onChange={e => setFilters({ ...filters, number: e.target.value })}
                  className="h-9 border-gray-300 bg-white focus:ring-[#D4C070]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-semibold">SKU do Produto</label>
                <Input
                  placeholder="Ex: BOLS-CHAN-PRE"
                  value={filters.sku}
                  onChange={e => setFilters({ ...filters, sku: e.target.value })}
                  className="h-9 border-gray-300 bg-white focus:ring-[#D4C070]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-semibold">Cliente</label>
                <AsyncSelect
                  value={filters.customer}
                  onChange={(id, lbl) => {
                    setFilters({ ...filters, customer: id || "" });
                    setCustomerLabel(lbl || "");
                  }}
                  fetcher={async (params) => {
                    if (!params.search || params.search.trim().length < 2) return [];
                    const res = await userService.getAll({ ...params, sort: 'first_name', order: 'asc' });
                    return res.data;
                  }}
                  initialDisplay={filters.customer ? customerLabel : ""}
                  placeholder="Buscar cliente por código, nome ou email..."
                  getOptionLabel={(u) => {
                    const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "";
                    return `[${u.code || u.id}] ${fullName}${u.email ? ` (${u.email})` : ""}`;
                  }}
                  renderOption={(u) => (
                    <div className="flex items-center gap-3 py-1.5 w-full">
                      <div className="flex items-center justify-center min-w-[55px] bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded border border-gray-700 uppercase tracking-tighter shadow-sm">
                        {u.code || u.id}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1 text-left">
                        <span className="font-bold text-gray-900 truncate">
                          {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.name}
                        </span>
                        {u.email && <span className="text-[10px] text-gray-500 truncate font-medium">{u.email}</span>}
                      </div>
                    </div>
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-semibold">Vendedora (Admin)</label>
                <select
                  value={filters.salesman}
                  onChange={e => setFilters({ ...filters, salesman: e.target.value })}
                  className="w-full h-9 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
                >
                  <option value="">Todas as vendedoras</option>
                  {admins.map(admin => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name}{admin.is_active === false || admin.deleted_at ? ' (Inativa)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-semibold">Status do Pedido</label>
                <div className="space-y-2 bg-gray-50 p-3 rounded-md border border-gray-200">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={filters.status.split(',').includes('5')}
                      onChange={e => {
                        const current = filters.status ? filters.status.split(',').filter(Boolean) : [];
                        const updated = e.target.checked ? [...current, '5'] : current.filter(s => s !== '5');
                        setFilters({ ...filters, status: updated.join(',') });
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-[#D4C070] focus:ring-[#D4C070]"
                    />
                    <span className="font-medium">Completo</span> (Pago)
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={filters.status.split(',').includes('7')}
                      onChange={e => {
                        const current = filters.status ? filters.status.split(',').filter(Boolean) : [];
                        const updated = e.target.checked ? [...current, '7'] : current.filter(s => s !== '7');
                        setFilters({ ...filters, status: updated.join(',') });
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-[#D4C070] focus:ring-[#D4C070]"
                    />
                    <span className="font-medium">Cancelados</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={filters.status.split(',').includes('2')}
                      onChange={e => {
                        const current = filters.status ? filters.status.split(',').filter(Boolean) : [];
                        const updated = e.target.checked ? [...current, '2'] : current.filter(s => s !== '2');
                        setFilters({ ...filters, status: updated.join(',') });
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-[#D4C070] focus:ring-[#D4C070]"
                    />
                    <span className="font-medium">Em Processamento</span> (Pendente / Não Pago)
                  </label>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">Se nenhum checkbox for marcado, o sistema aplica o filtro padrão (Completo + Cancelado).</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-semibold">Forma de Pagamento</label>
                <select
                  value={filters.gtway}
                  onChange={e => setFilters({ ...filters, gtway: e.target.value })}
                  className="w-full h-9 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
                >
                  <option value="">Todas as formas</option>
                  <option value="2">Cartão de Crédito</option>
                  <option value="3">Dinheiro</option>
                  <option value="4">Paypal (link)</option>
                  <option value="5">Depósito</option>
                  <option value="6">Dinheiro + Depósito</option>
                  <option value="7">Dinheiro + Cartão</option>
                  <option value="8">Dinheiro + Cheque</option>
                  <option value="9">Depósito + Cartão (Loja)</option>
                  <option value="10">Crédito Cliente (Loja)</option>
                  <option value="11">Crédito Fornecedora (Loja)</option>
                  <option value="12">Depósito + link PayPal</option>
                  <option value="13">Desconto / Fora do Closet</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-semibold">De (Data)</label>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={e => setFilters({ ...filters, dateFrom: e.target.value })}
                    className="h-9 border-gray-300 bg-white focus:ring-[#D4C070]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-semibold">Até (Data)</label>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={e => setFilters({ ...filters, dateTo: e.target.value })}
                    className="h-9 border-gray-300 bg-white focus:ring-[#D4C070]"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  handleClearFilters();
                  setShowFilters(false);
                }}
                className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
              >
                Limpar
              </Button>
              <Button
                onClick={() => {
                  handleApplyFilters();
                  setShowFilters(false);
                }}
                className="bg-[#D4C070] hover:bg-[#849D70] text-white"
              >
                Aplicar Filtros
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cards de Métricas (Informações dos Metadados da API) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200 bg-white">
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Vendas Finalizadas{dateLabel}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{totalSalesToShow}</p>
            <p className="text-xs text-gray-500">Pedidos pagos criados {descSuffix}</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200 bg-white">
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Receita Recebida{dateLabel}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">
              {revenueToShow.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
            <p className="text-xs text-[#849D70] font-medium">Exclui fretes e pedidos cancelados</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200 bg-white">
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Ticket Médio{dateLabel}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">
              {avgTicketToShow.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-gray-500">Valor médio por pedido pago {descSuffix}</p>
          </CardContent>
        </Card>
      </div>

      {/* Renderização de Carregamento / Vazio */}
      {loading ? (
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4C070] mx-auto"></div>
            <p className="mt-4 text-sm text-gray-400">Carregando listagem de vendas...</p>
          </CardContent>
        </Card>
      ) : sales.length === 0 ? (
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-12 text-center text-sm text-gray-400">
            Nenhuma venda encontrada para os filtros ou data especificada.
          </CardContent>
        </Card>
      ) : (
        /* LISTAGEM DE VENDAS ORGANIZADA (Estilo Produtos.tsx com quebra em 1340px) */
        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200 bg-white overflow-hidden">
          <CardContent className="p-0">
            <div className="divide-y divide-gray-150">
              {sales.map((sale) => (
                <div key={sale.id} className="flex flex-wrap min-[1340px]:flex-nowrap items-start min-[1340px]:items-center p-4 min-[1340px]:p-6 hover:bg-gray-50/40 transition-colors gap-4 min-[1340px]:gap-6">

                  {/* Coluna 1: ID e Status */}
                  <div className="w-full min-[1340px]:w-28 flex flex-col gap-1 flex-shrink-0">
                    <span className="text-sm font-extrabold text-gray-950 font-mono">#{sale.id}</span>
                    <Badge
                      className={
                        sale.status.id === 5 ? 'bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] py-0.5 w-max font-semibold' :
                          sale.status.id === 7 ? 'bg-rose-100 text-rose-800 border-rose-200 text-[10px] py-0.5 w-max font-semibold' :
                            sale.status.id === 2 ? 'bg-amber-100 text-amber-850 border-amber-200 text-[10px] py-0.5 w-max font-semibold' :
                              'bg-gray-105 text-gray-800 border-gray-200 text-[10px] py-0.5 w-max font-semibold'
                      }
                      variant="outline"
                    >
                      {sale.status.id === 2 ? 'Pendente / Não Pago' : sale.status.name}
                    </Badge>
                    <span className="text-[10px] text-gray-400 font-medium mt-0.5" title={sale.created_at.human}>Criado: {sale.created_at.timestamp}</span>
                    {sale.updated_at && sale.updated_at.timestamp !== sale.created_at.timestamp && (
                      <span className="text-[10px] text-[#849D70] font-medium mt-0.5" title={sale.updated_at.human}>
                        Atualizado: {sale.updated_at.timestamp}
                      </span>
                    )}
                  </div>

                  {/* Coluna 2: Cliente & Vendedor */}
                  <div className="w-full min-[1340px]:w-56 text-xs space-y-1 border-t min-[1340px]:border-t-0 pt-2 min-[1340px]:pt-0">
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Cliente & Vendedor</p>
                    {sale.customer?.id ? (
                      <div>
                        <p className="font-semibold text-gray-900 truncate">{sale.customer.name}</p>
                        <p className="text-gray-500 truncate text-[11px]">{sale.customer.email}</p>
                        <p className="text-gray-400 font-mono text-[10px]">{sale.customer.phone || 'Sem celular'}</p>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic">Cliente não cadastrado</p>
                    )}
                    <div className="pt-1.5 border-t border-gray-100 mt-1.5 text-[10px]">
                      <span className="text-gray-400">Vendedor: </span>
                      <span className="font-semibold text-gray-700">
                        {sale.salesman?.name
                          ? `${sale.salesman.name}${sale.salesman.is_inactive ? ' (Inativo)' : ''}`
                          : 'Site'}
                      </span>
                    </div>
                  </div>

                  {/* Coluna 3: Produtos Vendidos (Expande em largura total quando <= 1340px) */}
                  <div className="w-full min-[1340px]:flex-1 min-w-0 space-y-2 border-t min-[1340px]:border-t-0 pt-2 min-[1340px]:pt-0">
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mb-1">Itens do Pedido</p>
                    <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                      {sale.products && sale.products.map((prod: any, idx: number) => {
                        const productId = prod.id || prod.product_id;
                        const isSaleCanceled = Number(sale.status?.id ?? sale.status_id) === 7 || sale.status?.name === 'Cancelado';
                        const isProdCanceled = !!prod.canceled_at || !!prod.deleted_at || isSaleCanceled;
                        return (
                          <div key={idx} className={`flex items-start gap-2.5 text-xs ${isProdCanceled ? 'opacity-40 line-through' : ''}`}>
                            <ImageWithFallback
                              src={prod.image || ''}
                              alt={prod.name}
                              className="w-10 h-10 rounded border border-gray-200 object-cover bg-gray-50 flex-shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-semibold text-gray-900 break-words leading-tight">{prod.name}</span>
                                {productId && (
                                  <button
                                    type="button"
                                    onClick={() => window.open(`/editar-produto/${productId}`, '_blank')}
                                    title="Ver dados do produto"
                                    className="p-0.5 text-[#D4C070] hover:text-[#849D70] hover:bg-gray-100 rounded transition-colors cursor-pointer shrink-0 inline-flex items-center"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                              <p className="text-[10px] text-gray-400 font-mono mt-0.5 break-all">SKU: {prod.sku}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Coluna 4: Financeiro e Gateway */}
                  <div className="w-full min-[1340px]:w-56 text-xs space-y-1.5 min-[1340px]:text-right flex-shrink-0 border-t min-[1340px]:border-t-0 pt-2 min-[1340px]:pt-0">
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider min-[1340px]:text-right">Faturamento</p>
                    <div>
                      <p className="font-semibold text-gray-800">{sale.payment.gateway}</p>
                      <p className="text-gray-400 text-[10px] mt-0.5">Subtotal: {sale.subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                      {Number(sale.discount || 0) > 0 && (
                        <p className="text-emerald-600 text-[10px] font-medium">
                          Desconto {sale.discount_code ? `(${sale.discount_code})` : ''}: - {Number(sale.discount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                      )}
                      <p className="text-gray-400 text-[10px]">Frete: {(sale.shipping_total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                    </div>
                    <div className="pt-1 border-t border-gray-150 flex min-[1340px]:justify-end justify-between items-baseline gap-2 mt-1">
                      <span className="text-gray-400 text-[10px] min-[1340px]:inline hidden font-normal">Total:</span>
                      <span className="text-[#849D70] font-extrabold text-sm">
                        {sale.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </span>
                    </div>
                  </div>

                  {/* Coluna 5: Rastreio & Ações */}
                  <div className="w-full min-[1340px]:w-36 flex flex-col items-end min-[1340px]:items-center justify-center gap-1.5 flex-shrink-0 border-t min-[1340px]:border-t-0 pt-2 min-[1340px]:pt-0">
                    {/* Código de Rastreio */}
                    <div className="flex items-center gap-1 text-[10px] text-gray-400">
                      <span className="font-semibold">Rastreio:</span>
                      <span className="flex items-center gap-1.5">
                        <span className="font-mono bg-gray-100 px-1 py-0.5 rounded text-gray-700 font-semibold">{sale.shipping_code || '-'}</span>
                        <button
                          onClick={() => handleAbrirShipping(sale)}
                          title="Informar Rastreio"
                          className="cursor-pointer text-[#D4C070] hover:text-[#BCA850] transition-colors flex items-center"
                        >
                          <Edit3 className="h-3 w-3" />
                        </button>
                      </span>
                    </div>
                    {/* Botões de Ações */}
                    <div className="flex flex-col gap-2 w-full mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300 w-full justify-center h-8"
                      >
                        <a href={`/vendas/${sale.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 w-full">
                          <Eye className="h-3.5 w-3.5" />
                          <span>Visualizar</span>
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenEditModal(sale)}
                        className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300 w-full justify-center h-8 flex items-center gap-1.5"
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                        <span>Editar</span>
                      </Button>
                    </div>
                  </div>

                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center mt-6">
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1 || loading}
              onClick={() => handlePageChange(currentPage - 1)}
              className="border-gray-300"
            >
              {'<'}
            </Button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              if (totalPages > 6 && Math.abs(page - currentPage) > 2 && page !== 1 && page !== totalPages) {
                if (page === 2 || page === totalPages - 1) {
                  return <span key={page} className="px-1 text-gray-400">...</span>;
                }
                return null;
              }

              return (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  disabled={loading}
                  className={currentPage === page ? "bg-[#D4C070] text-white hover:bg-[#849D70]" : "border-gray-300"}
                >
                  {page}
                </Button>
              );
            })}

            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages || loading}
              onClick={() => handlePageChange(currentPage + 1)}
              className="border-gray-300"
            >
              {'>'}
            </Button>
          </div>
        </div>
      )}

      {/* Modal de Edição de Informações */}
      {selectedOrder && (
        <OrderInfoEditModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedOrder(null);
          }}
          orderInfo={selectedOrder}
          onSuccess={() => fetchSales(currentPage, searchTerm, activeFilters)}
        />
      )}

      {/* Modal Rastreio */}
      <Dialog open={shippingDialogOpen} onOpenChange={setShippingDialogOpen}>
        <DialogContent className="sm:max-w-[400px] bg-white border border-gray-150 p-6 rounded-xl shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">Informar Rastreio</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-2 block text-sm font-semibold text-gray-700">Código de Rastreio</Label>
            <Input
              type="text"
              placeholder="Ex: AB123456789BR"
              value={shippingCode}
              onChange={(e) => setShippingCode(e.target.value)}
              className="border-gray-300 focus:ring-[#D4C070]"
            />
          </div>
          <DialogFooter className="flex justify-end gap-2 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => setShippingDialogOpen(false)} disabled={isSubmittingShipping} className="border-gray-300">
              Cancelar
            </Button>
            <Button onClick={handleConfirmarShipping} disabled={isSubmittingShipping} className="bg-[#D4C070] hover:bg-[#849D70] text-white">
              {isSubmittingShipping ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

export default VendasPage;
